/* ============================================================
   Caliper UI primitives — CureBay Pharmacy Operator Console
   Dense, warm-paper, ink-navy. Tabler-style icons via lucide.
   Import from "../ui/caliper".
   ============================================================ */
import React from "react";
import { cn } from "../../lib/utils";
import { ChevronRight, X, Search, Inbox, Loader2 } from "lucide-react";

/* ---------- formatters ---------- */
export const fmtNum = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = typeof n === "string" ? Number(n) : n;
  if (Number.isNaN(v)) return String(n);
  return v.toLocaleString("en-IN");
};

export const fmtINR = (n) => {
  if (n === null || n === undefined || n === "") return "—";
  const v = typeof n === "string" ? Number(n) : n;
  if (Number.isNaN(v)) return String(n);
  return "₹" + v.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/* Map an order / lifecycle status string to a Pill variant. */
export const statusVariant = (raw) => {
  const s = String(raw || "").toLowerCase().trim();
  if (/(deliver|complete|paid|fresh|active|success|in stock|approved)/.test(s)) return "green";
  if (/(confirm)/.test(s)) return "green";
  if (/(cancel|reject|expired|fail|overdue|breach|damage)/.test(s)) return "red";
  if (/(pack|pending|hold|partial|soon|low|draft)/.test(s)) return "amber";
  if (/(ship|transfer|transit|dispatch)/.test(s)) return "transfer";
  if (/(receiv)/.test(s)) return "ink";
  if (/(enquir|new)/.test(s)) return "neutral";
  return "neutral";
};

/* ---------- Button ---------- */
const BTN_SIZES = {
  sm: "h-6 px-2 text-t-sm rounded-cb-sm gap-1",
  md: "h-[30px] px-3 text-t-md rounded-cb-sm gap-1.5",
  lg: "h-[38px] px-4 text-t-base rounded-cb-sm gap-2",
};
const BTN_VARIANTS = {
  primary: "bg-ink-600 text-fg-on-ink hover:bg-ink-700 active:bg-ink-800 border border-transparent",
  secondary: "bg-panel text-fg-strong border border-hairline hover:bg-surface-hover",
  ghost: "bg-transparent text-fg hover:bg-surface-hover border border-transparent",
  subtle: "bg-ink-50 text-ink-700 hover:bg-ink-100 border border-transparent",
  danger: "bg-red-500 text-white hover:bg-red-700 border border-transparent",
  "danger-soft": "bg-red-50 text-red-700 hover:bg-red-100 border border-transparent",
};

export function Button({
  variant = "secondary",
  size = "md",
  icon,
  iconRight,
  className,
  children,
  ...rest
}) {
  return (
    <button
      type="button"
      {...rest}
      className={cn(
        "inline-flex items-center justify-center font-medium whitespace-nowrap font-sans",
        "transition-colors duration-150 disabled:opacity-45 disabled:cursor-not-allowed",
        BTN_SIZES[size],
        BTN_VARIANTS[variant],
        className
      )}
    >
      {icon}
      {children}
      {iconRight}
    </button>
  );
}

/* ---------- IconButton ---------- */
export function IconButton({ icon, label, className, size = 28, ...rest }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      {...rest}
      style={{ width: size, height: size }}
      className={cn(
        "inline-flex items-center justify-center rounded-cb-sm text-fg-muted",
        "hover:bg-surface-hover hover:text-fg-strong transition-colors duration-150",
        "disabled:opacity-40 disabled:cursor-not-allowed",
        className
      )}
    >
      {icon}
    </button>
  );
}

/* ---------- Pill / status badge ---------- */
const PILL_VARIANTS = {
  neutral: "bg-neutral-75 text-neutral-600",
  fresh: "bg-green-50 text-green-700",
  green: "bg-green-50 text-green-700",
  soon: "bg-amber-50 text-amber-700",
  amber: "bg-amber-50 text-amber-700",
  expired: "bg-red-50 text-red-700",
  red: "bg-red-50 text-red-700",
  transfer: "bg-violet-50 text-violet-700",
  violet: "bg-violet-50 text-violet-700",
  ink: "bg-ink-50 text-ink-700",
};
const DOT_VARIANTS = {
  neutral: "bg-neutral-400",
  fresh: "bg-green-500",
  green: "bg-green-500",
  soon: "bg-amber-500",
  amber: "bg-amber-500",
  expired: "bg-red-500",
  red: "bg-red-500",
  transfer: "bg-violet-500",
  violet: "bg-violet-500",
  ink: "bg-ink-500",
};

export function Pill({ variant = "neutral", dot = false, className, children }) {
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 h-5 px-1.5 rounded-cb-xs",
        "text-[11px] font-semibold leading-none uppercase tracking-[0.04em] whitespace-nowrap",
        PILL_VARIANTS[variant] || PILL_VARIANTS.neutral,
        className
      )}
    >
      {dot && <span className={cn("w-1.5 h-1.5 rounded-full", DOT_VARIANTS[variant])} />}
      {children}
    </span>
  );
}

/* Convenience: status pill straight from a status string. */
export function StatusPill({ status, dot = true, className }) {
  if (!status && status !== 0) return <span className="text-fg-subtle">—</span>;
  return (
    <Pill variant={statusVariant(status)} dot={dot} className={className}>
      {String(status)}
    </Pill>
  );
}

/* small "NEW"-style tag */
export function Tag({ tone = "green", className, children }) {
  const tones = {
    green: "bg-green-500 text-white",
    ink: "bg-ink-600 text-white",
    amber: "bg-amber-500 text-white",
    red: "bg-red-500 text-white",
  };
  return (
    <span
      className={cn(
        "inline-flex items-center h-4 px-1.5 rounded-cb-xs text-[9px] font-semibold",
        "uppercase tracking-[0.06em] leading-none",
        tones[tone],
        className
      )}
    >
      {children}
    </span>
  );
}

/* ---------- Card ---------- */
export function Card({ className, children, ...rest }) {
  return (
    <div
      {...rest}
      className={cn(
        "bg-panel border border-hairline rounded-cb-md",
        className
      )}
    >
      {children}
    </div>
  );
}

/* card with an eyebrow header strip */
export function PanelCard({ title, action, className, bodyClassName, children }) {
  return (
    <Card className={cn("overflow-hidden", className)}>
      {(title || action) && (
        <div className="flex items-center justify-between px-3.5 py-2.5 border-b border-hairline-subtle">
          <span className="cb-eyebrow">{title}</span>
          {action}
        </div>
      )}
      <div className={bodyClassName}>{children}</div>
    </Card>
  );
}

/* ---------- StatCard ---------- */
export function StatCard({ label, value, sub, accent, icon, className }) {
  return (
    <Card className={cn("p-3.5 flex flex-col gap-1.5", className)}>
      <div className="flex items-center justify-between">
        <span className="cb-eyebrow">{label}</span>
        {icon && <span className="text-fg-subtle">{icon}</span>}
      </div>
      <span
        className="font-mono tabular-nums text-t-3xl font-semibold leading-tight"
        style={{ color: accent || "var(--fg-strong)" }}
      >
        {value}
      </span>
      {sub && <span className="text-t-xs text-fg-muted leading-snug">{sub}</span>}
    </Card>
  );
}

/* compact inline KPI box (tinted) */
const KPI_TONES = {
  ink: "bg-ink-50 text-ink-700 border-ink-100",
  green: "bg-green-50 text-green-700 border-green-100",
  amber: "bg-amber-50 text-amber-700 border-amber-100",
  red: "bg-red-50 text-red-700 border-red-100",
  neutral: "bg-surface-subtle text-fg-default border-hairline",
};
export function KpiBox({ label, value, tone = "ink", className }) {
  return (
    <div
      className={cn(
        "flex items-center justify-between gap-2 px-3 py-2 rounded-cb-sm border",
        KPI_TONES[tone],
        className
      )}
    >
      <span className="text-[10px] font-semibold uppercase tracking-[0.08em]">{label}</span>
      <span className="font-mono tabular-nums text-[20px] font-bold leading-none">
        {value}
      </span>
    </div>
  );
}

/* ---------- Inputs ---------- */
export function Input({ className, mono = false, icon, prefix, suffix, ...rest }) {
  return (
    <div
      className={cn(
        "flex items-stretch border border-hairline rounded-cb-sm bg-panel overflow-hidden",
        "focus-within:border-ink-500 focus-within:shadow-cb-focus transition-shadow",
        className
      )}
    >
      {prefix && (
        <span className="flex items-center px-2 bg-sunken border-r border-hairline-subtle font-mono text-t-xs text-fg-muted">
          {prefix}
        </span>
      )}
      {icon && <span className="flex items-center pl-2.5 text-fg-muted">{icon}</span>}
      <input
        {...rest}
        className={cn(
          "flex-1 min-w-0 bg-transparent border-0 outline-none h-7 px-2.5 text-fg-default",
          "placeholder:text-fg-subtle",
          mono ? "font-mono text-t-sm" : "text-t-md"
        )}
      />
      {suffix && (
        <span className="flex items-center px-2 text-t-xs text-fg-muted">{suffix}</span>
      )}
    </div>
  );
}

export function SearchInput({ className, ...rest }) {
  return (
    <Input
      icon={<Search size={13} strokeWidth={1.75} />}
      className={className}
      placeholder="Search…"
      {...rest}
    />
  );
}

export function Select({ className, children, ...rest }) {
  return (
    <select
      {...rest}
      className={cn(
        "h-7 pl-2.5 pr-7 border border-hairline rounded-cb-sm bg-panel text-t-md text-fg-default",
        "font-sans cursor-pointer outline-none appearance-none",
        "focus:border-ink-500 focus:shadow-cb-focus transition-shadow",
        "bg-[length:14px] bg-[right_6px_center] bg-no-repeat",
        className
      )}
      style={{
        backgroundImage:
          "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%236f6b5e' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'><polyline points='6 9 12 15 18 9'/></svg>\")",
      }}
    >
      {children}
    </select>
  );
}

/* labelled field wrapper */
export function Field({ label, hint, error, required, children, className }) {
  return (
    <label className={cn("flex flex-col gap-1", className)}>
      {label && (
        <span className="text-t-sm font-medium text-fg-default">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </span>
      )}
      {children}
      {error ? (
        <span className="text-t-xs text-red-700">{error}</span>
      ) : hint ? (
        <span className="cb-caption">{hint}</span>
      ) : null}
    </label>
  );
}

/* ---------- Kbd ---------- */
export function Kbd({ children, className }) {
  return (
    <span
      className={cn(
        "inline-flex items-center font-mono text-[10px] px-1.5 py-0.5 leading-none",
        "border border-hairline rounded-cb-xs bg-panel text-fg-default",
        className
      )}
    >
      {children}
    </span>
  );
}

/* ---------- Page / Section headers ---------- */
export function PageHeader({ title, sub, actions, className }) {
  return (
    <div className={cn("flex flex-wrap items-start justify-between gap-3", className)}>
      <div className="min-w-0">
        <h1 className="text-t-2xl sm:text-t-3xl font-semibold text-fg-strong leading-tight tracking-[-0.01em] m-0">
          {title}
        </h1>
        {sub && <div className="text-t-sm text-fg-muted mt-1">{sub}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

export function SectionHeader({ title, sub, actions, className }) {
  return (
    <div className={cn("flex flex-wrap items-center justify-between gap-3", className)}>
      <div className="min-w-0">
        <h2 className="text-t-xl font-semibold text-fg-strong leading-snug m-0">{title}</h2>
        {sub && <div className="text-t-sm text-fg-muted mt-0.5">{sub}</div>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/* ---------- Toolbar ---------- */
export function Toolbar({ className, children }) {
  return (
    <div
      className={cn(
        "flex items-center gap-2 flex-wrap px-3 py-2 bg-panel border border-hairline rounded-cb-md",
        className
      )}
    >
      {children}
    </div>
  );
}

/* ---------- Tabs ---------- */
export function Tabs({ tabs, value, onChange, className }) {
  return (
    <div className={cn("flex items-center gap-0 border-b border-hairline overflow-x-auto cb-scroll", className)}>
      {tabs.map((t) => {
        const active = value === t.id;
        return (
          <button
            key={t.id}
            type="button"
            onClick={() => onChange(t.id)}
            className={cn(
              "inline-flex items-center gap-1.5 px-3.5 py-2 -mb-px text-t-sm font-semibold shrink-0 whitespace-nowrap",
              "border-b-2 transition-colors duration-150",
              active
                ? "text-ink-700 border-ink-600"
                : "text-fg-muted border-transparent hover:text-fg-default"
            )}
          >
            {t.label}
            {t.count !== undefined && t.count !== null && (
              <span
                className={cn(
                  "font-mono text-[10px] font-medium px-1.5 py-0.5 rounded-full leading-none",
                  active ? "bg-ink-50 text-ink-700" : "bg-sunken text-fg-muted"
                )}
              >
                {t.count}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Data table ---------- */
export function Table({ columns = [], children, sticky = true, className, minWidth }) {
  return (
    <div
      className={cn(
        "bg-panel border border-hairline rounded-cb-md overflow-auto cb-scroll",
        className
      )}
    >
      <table className="w-full border-collapse" style={{ minWidth }}>
        <thead className={cn(sticky && "sticky top-0 z-[2]")}>
          <tr>
            {columns.map((c, i) => (
              <th
                key={i}
                style={{ width: c.width, textAlign: c.align || "left" }}
                className={cn(
                  "bg-surface-subtle cb-th px-3 py-2 whitespace-nowrap",
                  "border-b border-hairline"
                )}
              >
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>{children}</tbody>
      </table>
    </div>
  );
}

export function Tr({ children, selected, onClick, className }) {
  return (
    <tr
      onClick={onClick}
      className={cn(
        "transition-colors duration-100 border-b border-hairline-subtle last:border-b-0",
        onClick && "cursor-pointer",
        selected
          ? "bg-surface-selected shadow-[inset_2px_0_0_var(--ink-600)]"
          : "hover:bg-surface-hover",
        className
      )}
    >
      {children}
    </tr>
  );
}

export function Td({ children, mono, right, center, className, ...rest }) {
  return (
    <td
      {...rest}
      style={{ height: 32 }}
      className={cn(
        "px-3 text-t-sm text-fg-default align-middle whitespace-nowrap",
        mono && "font-mono tabular-nums",
        right && "text-right",
        center && "text-center",
        className
      )}
    >
      {children}
    </td>
  );
}

/* full-width empty / no-data cell */
export function TableEmpty({ colSpan, icon, message = "No results found", hint }) {
  return (
    <tr>
      <td colSpan={colSpan} className="py-12">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <span className="text-fg-subtle">
            {icon || <Inbox size={26} strokeWidth={1.5} />}
          </span>
          <div className="text-t-md font-medium text-fg-default">{message}</div>
          {hint && <div className="cb-caption max-w-xs">{hint}</div>}
        </div>
      </td>
    </tr>
  );
}

/* ---------- Empty state (standalone) ---------- */
export function EmptyState({ icon, title, hint, action, className }) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-2.5 text-center py-16 px-6",
        className
      )}
    >
      <span className="text-fg-subtle mb-1">
        {icon || <Inbox size={30} strokeWidth={1.5} />}
      </span>
      <div className="text-t-lg font-semibold text-fg-strong">{title}</div>
      {hint && <div className="text-t-sm text-fg-muted max-w-sm">{hint}</div>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}

/* ---------- Spinner / skeleton ---------- */
export function Spinner({ size = 16, className }) {
  return (
    <Loader2
      size={size}
      strokeWidth={2}
      className={cn("animate-spin text-fg-muted", className)}
    />
  );
}

export function SkeletonRows({ rows = 6, cols = 5 }) {
  return Array.from({ length: rows }).map((_, r) => (
    <tr key={r} className="border-b border-hairline-subtle">
      {Array.from({ length: cols }).map((__, c) => (
        <td key={c} className="px-3" style={{ height: 32 }}>
          <div
            className="cb-skeleton h-3 rounded-cb-xs"
            style={{ width: c === 0 ? "60%" : c === cols - 1 ? "40%" : "75%" }}
          />
        </td>
      ))}
    </tr>
  ));
}

/* ---------- Modal ---------- */
export function Modal({ open, onClose, title, sub, children, footer, width = 520 }) {
  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 cb-fade-in"
      style={{ background: "var(--bg-overlay)" }}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div
        className="bg-panel border border-hairline-subtle rounded-t-cb-lg sm:rounded-cb-lg shadow-cb-lg w-full cb-animate-in flex flex-col max-h-[92dvh] sm:max-h-[88vh]"
        style={{ maxWidth: width }}
      >
        {(title || onClose) && (
          <div className="flex items-start justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-hairline-subtle">
            <div>
              {title && (
                <h3 className="text-t-lg font-semibold text-fg-strong m-0">{title}</h3>
              )}
              {sub && <div className="text-t-sm text-fg-muted mt-0.5">{sub}</div>}
            </div>
            {onClose && (
              <IconButton
                label="Close"
                onClick={onClose}
                icon={<X size={16} strokeWidth={1.75} />}
              />
            )}
          </div>
        )}
        <div className="px-4 sm:px-5 py-4 overflow-auto cb-scroll">{children}</div>
        {footer && (
          <div className="flex flex-wrap items-center justify-end gap-2 px-4 sm:px-5 py-3 border-t border-hairline-subtle bg-surface-subtle">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------- misc ---------- */
export function Divider({ vertical, className }) {
  return vertical ? (
    <span className={cn("w-px self-stretch bg-hairline", className)} />
  ) : (
    <div className={cn("h-px w-full bg-hairline-subtle", className)} />
  );
}

export function MetaDot() {
  return <span className="text-fg-subtle mx-1.5">·</span>;
}

/* chevron used to mark a clickable row */
export function RowChevron() {
  return <ChevronRight size={13} strokeWidth={1.75} className="text-fg-subtle" />;
}
